import { useEffect } from "react";
import { COMPANY, SERVICES, BLOG_POSTS } from "../../content/siteContent";

const SITE_URL =
  process.env.REACT_APP_SITE_URL || "https://www.zawixbusinessconsultants.com";
const DEFAULT_IMAGE = `${SITE_URL}/images/logo/logo.png`;
const DEFAULT_TITLE =
  "SECP, FBR, PEC & Tax Consultants Islamabad | Zawix";
const DEFAULT_DESCRIPTION =
  "Zawix Business Consultants in Blue Area Islamabad provides SECP company registration, FBR NTN and sales tax registration, income tax return filing, PEC, AEDB/PPIB, audit, import export, and regulatory compliance services across Pakistan.";
const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const ensureLink = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#organization`,
  name: COMPANY.name,
  url: SITE_URL,
  logo: DEFAULT_IMAGE,
  image: DEFAULT_IMAGE,
  description:
    "Business consultants in Blue Area Islamabad providing SECP company registration, FBR tax registration, income tax return filing, audit reports, PEC, AEDB/PPIB, licensing, and regulatory compliance support across Pakistan.",
  telephone: COMPANY.phones[0]?.number,
  email: COMPANY.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: COMPANY.addressLines.slice(0, 2).join(", "),
    addressLocality: "Islamabad",
    addressRegion: "Islamabad Capital Territory",
    addressCountry: "PK",
  },
  areaServed: [
    { "@type": "Country", name: "Pakistan" },
    { "@type": "City", name: "Islamabad" },
    { "@type": "City", name: "Rawalpindi" },
    { "@type": "City", name: "Lahore" },
    { "@type": "City", name: "Karachi" },
  ],
  openingHours: "Mo-Fr 09:00-18:00",
  contactPoint: [
    ...COMPANY.phones.map((phone) => ({
      "@type": "ContactPoint",
      telephone: phone.number,
      contactType: "customer service",
      areaServed: "PK",
      availableLanguage: ["en", "ur"],
    })),
    {
      "@type": "ContactPoint",
      telephone: COMPANY.whatsappContact.number,
      contactType: "WhatsApp",
      areaServed: "PK",
      availableLanguage: ["en", "ur"],
    },
  ],
  sameAs: ["https://www.facebook.com/share/1Axq462wMY/"],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Business Registration, Tax and Compliance Services",
    itemListElement: SERVICES.map((service) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: service.title,
        url: `${SITE_URL}/services/${service.slug}`,
        areaServed: "Pakistan",
      },
    })),
  },
});

const buildServiceSchema = (title, description, canonicalUrl, service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: title,
  description,
  provider: {
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#organization`,
    name: COMPANY.name,
    url: SITE_URL,
    telephone: COMPANY.phones[0]?.number,
  },
  areaServed: "Pakistan",
  url: canonicalUrl,
  serviceType: service?.title || title,
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    areaServed: "Pakistan",
  },
});

const buildArticleSchema = (post, canonicalUrl) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "@id": `${canonicalUrl}#article`,
  headline: post.title,
  description: post.metaDescription || post.excerpt,
  image: `${SITE_URL}/images/services-details/${post.image}`,
  datePublished: post.publishDate,
  dateModified: post.updatedDate || post.publishDate,
  url: canonicalUrl,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": canonicalUrl,
  },
  author: {
    "@type": "Person",
    name: "Faisal Shabbir",
    jobTitle: "CEO",
  },
  publisher: {
    "@type": "Organization",
    name: COMPANY.name,
    logo: {
      "@type": "ImageObject",
      url: DEFAULT_IMAGE,
    },
  },
  keywords: (post.keywords || []).join(", "),
  articleSection: post.category,
});

export const getPageSeo = ({ pathname, title, description }) => {
  const canonicalPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const pageTitle = title ? `${title} | ${COMPANY.name}` : DEFAULT_TITLE;
  const pageDescription = description || DEFAULT_DESCRIPTION;

  return {
    canonicalUrl,
    pageTitle,
    pageDescription,
  };
};

export const getServiceSeo = (slug) => {
  const service = SERVICES.find((item) => item.slug === slug);

  if (!service) {
    return null;
  }

  return {
    title: `${service.title} in Islamabad & Pakistan`,
    description: service.cardText || service.intro,
    service,
  };
};

export const getBlogSeo = (slug) => {
  const post = BLOG_POSTS.find((item) => item.slug === slug);

  if (!post) {
    return null;
  }

  return {
    title: post.metaTitle || post.title,
    description: post.metaDescription || post.excerpt,
    post,
  };
};

export const Seo = ({
  title,
  description,
  robots = "index, follow, max-image-preview:large",
  pathname = "/",
  schemaType = "organization",
}) => {
  useEffect(() => {
    const { canonicalUrl, pageTitle, pageDescription } = getPageSeo({
      pathname,
      title,
      description,
    });

    document.title = pageTitle;

    let schema = buildOrganizationSchema();
    let ogImage = DEFAULT_IMAGE;
    let ogImageAlt = `${COMPANY.name} logo`;
    if (schemaType === "service") {
      const service = SERVICES.find((item) => canonicalUrl.endsWith(`/services/${item.slug}`));
      schema = buildServiceSchema(
        title || service?.title || COMPANY.name,
        pageDescription,
        canonicalUrl,
        service
      );
    } else if (schemaType === "article") {
      const post = BLOG_POSTS.find((item) => canonicalUrl.endsWith(`/blog/${item.slug}`));
      if (post) {
        schema = buildArticleSchema(post, canonicalUrl);
        ogImage = `${SITE_URL}/images/services-details/${post.image}`;
        ogImageAlt = post.imageAlt || post.title;
      }
    }

    ensureMeta('meta[name="description"]', {
      name: "description",
      content: pageDescription,
    });
    ensureMeta('meta[name="robots"]', {
      name: "robots",
      content: robots,
    });
    ensureMeta('meta[name="author"]', {
      name: "author",
      content: COMPANY.name,
    });
    ensureMeta('meta[property="og:title"]', {
      property: "og:title",
      content: pageTitle,
    });
    ensureMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: COMPANY.name,
    });
    ensureMeta('meta[property="og:description"]', {
      property: "og:description",
      content: pageDescription,
    });
    ensureMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: "en_PK",
    });
    ensureMeta('meta[property="og:type"]', {
      property: "og:type",
      content: schemaType === "article" ? "article" : "website",
    });
    ensureMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
    ensureMeta('meta[property="og:image"]', {
      property: "og:image",
      content: ogImage,
    });
    ensureMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: ogImageAlt,
    });
    ensureMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    ensureMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: pageTitle,
    });
    ensureMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: pageDescription,
    });
    ensureMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: ogImage,
    });
    ensureMeta('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: ogImageAlt,
    });
    ensureLink('link[rel="canonical"]', {
      rel: "canonical",
      href: canonicalUrl,
    });

    let script = document.getElementById("seo-json-ld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "seo-json-ld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  }, [description, robots, pathname, schemaType, title]);

  return null;
};
